export const BRAIN_APP_VERSION = 'cognitive-lab-v2.0.2';
export const CONSENT_VERSION = 'brain-data-v0.1';
export const PRIVACY_NOTICE_VERSION = 'privacy-v0.1-draft';

const CONSENT_KEY = 'uos.brain.consent.v0.1';
const SESSION_KEY = 'uos.brain.session.v0.1';
const CONSENT_RECORDED_KEY = 'uos.brain.consent-recorded.v0.1';

function browserStorage() {
  return typeof window === 'undefined' ? null : window.localStorage;
}

function uuid() {
  return globalThis.crypto?.randomUUID?.() || `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function clean(value, max = 180) {
  return String(value || '').trim().slice(0, max) || null;
}

export function readConsent() {
  return browserStorage()?.getItem(CONSENT_KEY) === 'granted';
}

export function collectAttribution(locationLike = globalThis.location, documentLike = globalThis.document) {
  const params = new URLSearchParams(locationLike?.search || '');
  let referrerOrigin = null;
  try {
    referrerOrigin = documentLike?.referrer ? new URL(documentLike.referrer).origin : null;
  } catch {
    referrerOrigin = null;
  }
  return {
    entry_path: clean(locationLike?.pathname, 300),
    referrer_origin: clean(referrerOrigin, 300),
    utm_source: clean(params.get('utm_source')),
    utm_medium: clean(params.get('utm_medium')),
    utm_campaign: clean(params.get('utm_campaign')),
    utm_content: clean(params.get('utm_content')),
    utm_term: clean(params.get('utm_term'))
  };
}

export function responseRows(sessionId, instrument, state) {
  return instrument.questions.map(question => {
    const answer = state.answers[question.id] || { selected: [], ownWords: '' };
    return {
      session_id: sessionId,
      question_id: question.id,
      selected_options: answer.selected.slice(),
      own_words: String(answer.ownWords || ''),
      first_answered_at: answer.selected.length ? new Date().toISOString() : null
    };
  });
}

export function modelRows(runId, pkg, reviews = {}) {
  return {
    constructs: pkg.cognitiveModel.constructs.map(construct => ({
      model_run_id: runId,
      construct_id: construct.constructId,
      category: construct.category,
      score: construct.estimate.score,
      confidence: construct.estimate.confidence,
      evidence: construct.evidence,
      alternative_explanations: construct.alternativeExplanations,
      user_review: reviews[construct.constructId] || null,
      untested: construct.untested !== false
    })),
    rules: pkg.adapterPlan.rules.map(rule => ({
      model_run_id: runId,
      rule_id: rule.ruleId,
      construct_id: rule.constructId,
      mode: rule.mode,
      status: rule.status,
      behavior: rule.behavior,
      guardrail: rule.guardrail,
      reason: rule.reason,
      basis: rule.basis
    }))
  };
}

function configuredConnection() {
  const env = import.meta.env || {};
  const url = env.VITE_SUPABASE_URL || globalThis.UOS_SUPABASE?.url;
  const key = env.VITE_SUPABASE_PUBLISHABLE_KEY || globalThis.UOS_SUPABASE?.publishableKey;
  if (!url || !key || !String(url).startsWith('https://')) return null;
  return { url, key };
}

class BrainStore {
  constructor() {
    this.connection = configuredConnection();
    this.client = null;
    this.status = this.connection ? 'local-only' : 'not-configured';
    this.listeners = new Set();
    this.timer = null;
    this.pendingDraft = null;
  }

  isConfigured() { return Boolean(this.connection); }
  getStatus() { return this.status; }
  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.status);
    return () => this.listeners.delete(listener);
  }
  setStatus(status) {
    this.status = status;
    this.listeners.forEach(listener => listener(status));
  }

  async ensureClient() {
    if (!this.connection) return null;
    if (this.client) return this.client;
    const { createClient } = await import('@supabase/supabase-js');
    this.client = createClient(this.connection.url, this.connection.key, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true }
    });
    return this.client;
  }

  sessionId() {
    const storage = browserStorage();
    let id = storage?.getItem(SESSION_KEY);
    if (!id) {
      id = uuid();
      storage?.setItem(SESSION_KEY, id);
    }
    return id;
  }

  async user() {
    const client = await this.ensureClient();
    if (!client) return null;
    const { data: current, error: currentError } = await client.auth.getSession();
    if (currentError) throw currentError;
    if (current.session?.user) return current.session.user;
    const { data, error } = await client.auth.signInAnonymously();
    if (error) throw error;
    return data.user;
  }

  async ensureSession(instrument) {
    const client = await this.ensureClient();
    if (!client || !readConsent()) return null;
    this.setStatus('connecting');
    const user = await this.user();
    const sessionId = this.sessionId();
    const attribution = collectAttribution();
    const now = new Date().toISOString();
    const metadata = {
      userAgentFamily: clean(globalThis.navigator?.userAgent?.split(' ').slice(0, 3).join(' '), 120),
      language: clean(globalThis.navigator?.language, 40)
    };

    let result = await client.from('brain_participants').upsert({
      user_id: user.id,
      last_seen_at: now,
      first_touch: attribution,
      client_metadata: metadata
    }, { onConflict: 'user_id', ignoreDuplicates: true });
    if (result.error) throw result.error;
    result = await client.from('brain_participants').update({
      last_seen_at: now,
      client_metadata: metadata
    }).eq('user_id', user.id);
    if (result.error) throw result.error;

    if (browserStorage()?.getItem(CONSENT_RECORDED_KEY) !== CONSENT_VERSION) {
      result = await client.from('brain_consents').insert({
        user_id: user.id,
        decision: 'granted',
        consent_version: CONSENT_VERSION,
        privacy_notice_version: PRIVACY_NOTICE_VERSION,
        scopes: ['assessment_storage', 'model_generation', 'product_analytics']
      });
      if (result.error) throw result.error;
      browserStorage()?.setItem(CONSENT_RECORDED_KEY, CONSENT_VERSION);
    }

    result = await client.from('brain_sessions').upsert({
      id: sessionId,
      user_id: user.id,
      instrument_id: instrument.instrumentId,
      instrument_version: instrument.instrumentVersion,
      model_version: instrument.modelVersion || '0.2',
      app_version: BRAIN_APP_VERSION,
      status: 'in_progress',
      locale: clean(globalThis.navigator?.language, 40),
      timezone: clean(Intl.DateTimeFormat().resolvedOptions().timeZone, 80),
      ...attribution
    }, { onConflict: 'id', ignoreDuplicates: false });
    if (result.error) throw result.error;
    return { user, sessionId };
  }

  async setConsent(granted, instrument) {
    browserStorage()?.setItem(CONSENT_KEY, granted ? 'granted' : 'revoked');
    if (!this.connection) {
      this.setStatus('not-configured');
      return;
    }
    if (granted) {
      try {
        await this.ensureSession(instrument);
        this.setStatus('synced');
      } catch (error) {
        console.error('Brain database consent setup failed', error);
        this.setStatus('error');
      }
      return;
    }
    clearTimeout(this.timer);
    this.pendingDraft = null;
    try {
      const client = await this.ensureClient();
      const { data } = await client.auth.getSession();
      if (data.session?.user) {
        await client.from('brain_consents').insert({
          user_id: data.session.user.id,
          decision: 'revoked',
          consent_version: CONSENT_VERSION,
          privacy_notice_version: PRIVACY_NOTICE_VERSION,
          scopes: ['assessment_storage', 'model_generation', 'product_analytics']
        });
      }
    } finally {
      this.setStatus('local-only');
    }
  }

  queueDraft(instrument, state) {
    if (!this.connection || !readConsent()) return;
    this.pendingDraft = { instrument, state };
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.flushDraft(), 500);
  }

  async flushDraft() {
    const pending = this.pendingDraft;
    this.pendingDraft = null;
    if (!pending || !readConsent()) return;
    try {
      const identity = await this.ensureSession(pending.instrument);
      if (!identity) return;
      const rows = responseRows(identity.sessionId, pending.instrument, pending.state)
        .filter(row => row.selected_options.length || row.own_words.trim());
      if (rows.length) {
        const { error } = await this.client.from('brain_responses').upsert(rows, {
          onConflict: 'session_id,question_id',
          ignoreDuplicates: false
        });
        if (error) throw error;
      }
      this.setStatus('synced');
    } catch (error) {
      console.error('Brain database draft sync failed', error);
      this.setStatus('error');
    }
  }

  async saveModel(instrument, state, pkg) {
    if (!this.connection || !readConsent()) return;
    try {
      await this.flushDraft();
      const identity = await this.ensureSession(instrument);
      if (!identity) return;
      const runId = uuid();
      let result = await this.client.from('brain_model_runs').insert({
        id: runId,
        session_id: identity.sessionId,
        model_version: pkg.cognitiveModel.modelVersion,
        status: 'hypothesis',
        raw_package: pkg,
        generated_at: pkg.exportedAt
      });
      if (result.error) throw result.error;
      const rows = modelRows(runId, pkg, state.reviews);
      result = await this.client.from('brain_construct_estimates').insert(rows.constructs);
      if (result.error) throw result.error;
      result = await this.client.from('brain_adapter_rules').insert(rows.rules);
      if (result.error) throw result.error;
      result = await this.client.from('brain_sessions').update({
        status: 'completed',
        completed_at: new Date().toISOString()
      }).eq('id', identity.sessionId);
      if (result.error) throw result.error;
      await this.event('model_built', { constructCount: rows.constructs.length, ruleCount: rows.rules.length }, identity);
      this.setStatus('synced');
    } catch (error) {
      console.error('Brain database model sync failed', error);
      this.setStatus('error');
    }
  }

  async event(eventName, payload = {}, identity = null, questionId = null) {
    if (!this.connection || !readConsent()) return;
    try {
      const current = identity || await this.ensureSession({
        instrumentId: '3fold.u-cognitive-workstyle',
        instrumentVersion: '0.2',
        modelVersion: '0.2'
      });
      if (!current) return;
      const { error } = await this.client.from('brain_events').insert({
        user_id: current.user.id,
        session_id: current.sessionId,
        event_name: eventName,
        question_id: questionId,
        payload
      });
      if (error) throw error;
    } catch (error) {
      console.error('Brain database event sync failed', error);
      this.setStatus('error');
    }
  }

  async abandonSession() {
    if (!this.connection || !readConsent()) return;
    await this.ensureClient();
    const id = browserStorage()?.getItem(SESSION_KEY);
    if (id) await this.client.from('brain_sessions').update({ status: 'abandoned' }).eq('id', id);
    browserStorage()?.removeItem(SESSION_KEY);
  }
}

export const brainStore = new BrainStore();
